import React, { memo, useContext } from "react"
import { format } from "date-fns"
import { useDateFnsLocale } from "@/lib/i18n/format"
import { useAppSelector } from "@/reducers/hooks"
import { type Message as MessageType } from "@/reducers/messagesSlice"

import { Message, MessageText } from "@/components/ui/message/index"
import { useMessageData } from "@/components/chat/useMessageData"
import { selectSettings } from "@/reducers/settingsSlice"
import useMessageActions from "./useMessageActions"
import type { ComposerSurface } from "@/reducers/chatRepliesSlice"
import { cn } from "@/lib/utils"
import useReplyMessage from "@/components/chat/useReplyMessage"
import { MessageForwarded } from "@/components/ui/message/message-forwarded"
import {
  MessageReply,
  MessageReplyFallback,
} from "@/components/ui/message/message-reply"
import { MessageBody } from "@/components/ui/message/message-body"
import useMessageStatus from "@/components/chat/useMessageStatus"
import { isMobile } from "@/utils/detectDevice"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { MESSAGE_ROW_INSET } from "@/components/ui/message/rowInset"
import { useFeatureFlagEnabled } from "posthog-js/react"
import IncomingTranslation from "@/components/chat/IncomingTranslation"

const BROWSER_INCOMING_TRANSLATION_FLAG = "browser-incoming-translation"

type GenericMessageProps = {
  message: MessageType
  grouped?: boolean
  isFirstInGrouped?: boolean
  isLastInGrouped?: boolean
  interactive?: boolean
  showReplay?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
  composerSurface?: ComposerSurface
}

const GenericMessage: React.FC<GenericMessageProps> = ({
  message,
  grouped = false,
  isLastInGrouped = false,
  isFirstInGrouped = false,
  interactive = true,
  showReplay = true,
  actionButtonsVisible,
  hideActionButtons,
  composerSurface,
}) => {
  const dateFnsLocale = useDateFnsLocale()
  const messageData = useMessageData(message)
  const status = useMessageStatus(message)
  const actions = useMessageActions(
    message,
    messageData.displayBody,
    composerSurface,
  )
  const settings = useAppSelector(selectSettings)
  const browserTranslationEnabled =
    useFeatureFlagEnabled(BROWSER_INCOMING_TRANSLATION_FLAG) === true
  const { isForwardingMode } = useContext(ChatHistoryContext)

  const replyMessage = useReplyMessage(message)

  const containsReactions = messageData.reactions.length > 0
  const containsReplyMessage = Boolean(replyMessage)
  const containsForwardedMessage = Boolean(message.forwarded)
  const containsFallbackReplyMessage = message.replyMessage && !replyMessage
  const containsTranslation = message.translation && settings.showTranslations
  // An outgoing message the dispatcher typed in their own language and we
  // machine-translated before sending. `body` is what the driver received;
  // `originalBody` is what the dispatcher actually wrote. Deliberately not
  // gated behind a feature flag: a colleague without it must still be able
  // to read what a colleague with it typed.
  const containsOutgoingTranslation = Boolean(message.originalBody)
  // Driver rooms print the full date next to the hour, always visible —
  // elsewhere the timestamp stays HH:mm with the full date on hover.
  // Driver-room typography belongs to the workspace layout; the same room in
  // the old layout renders the way it always did.
  const inWorkspace = useIsDriverWorkspace()
  const isDriverRoomChat = inWorkspace && Boolean(messageData.driverParticipant)

  // The dispatcher's own "Slick AI translated it" badge/toggle isn't useful
  // on their own outgoing messages — only on the driver's.
  const isDispatcherOwnMessage = isDriverRoomChat && !messageData.isFromDriver

  // `internal` is a per-message flag, not a sender check — the dispatcher's
  // own message can be internal too (addressed to Slick AI, not the driver).
  // Reuse the exact user-bubble style from the general Slick AI chat tab
  // (AIChatMessage) so both sides of that side-channel look the same there
  // as they do here.
  if (inWorkspace && message.internal) {
    return (
      <div className={cn("my-10 flex justify-start", MESSAGE_ROW_INSET)}>
        <div className="w-fit max-w-[85%] rounded-lg bg-blue-100 px-3 py-2 dark:bg-blue-900">
          <p className="whitespace-pre-wrap text-base leading-relaxed text-blue-700 dark:text-blue-200">
            {message.body}
          </p>
        </div>
      </div>
    )
  }

  return (
    <Message
      message={message}
      replyMessage={replyMessage}
      variant={messageData.variant}
      showAvatar={isLastInGrouped && !isForwardingMode}
      onReply={!isForwardingMode ? actions.handleReply : undefined}
      onForward={!isForwardingMode ? actions.handleForward : undefined}
      onDelete={!isForwardingMode ? actions.handleDelete : undefined}
      onReact={!isForwardingMode ? actions.handleReact : undefined}
      onPin={!isForwardingMode ? actions.handlePin : undefined}
      onUnPin={!isForwardingMode ? actions.handleUnPin : undefined}
      onCopy={!isForwardingMode && isMobile ? actions.handleCopy : undefined}
      onUnlinkDocument={
        !isForwardingMode ? actions.handleUnlinkDocument : undefined
      }
      onFindDeliveryOrder={
        !isForwardingMode ? actions.handleFindDeliveryOrder : undefined
      }
      interactive={interactive}
      actionButtonsVisible={actionButtonsVisible}
      hideActionButtons={hideActionButtons}
      className={cn({
        "mb-1": (grouped || isFirstInGrouped) && !isLastInGrouped,
        "mb-7": containsReactions,
      })}
    >
      <Message.Avatar
        src={messageData.avatarSrc}
        name={messageData.authorName}
        fallback={messageData.authorInitials}
        avatarColor={messageData.avatarColor}
      />

      <Message.Bubble withDriver={messageData.isFromDriver}>
        <Message.Header
          author={messageData.authorLabel}
          timestamp={
            isDriverRoomChat
              ? format(new Date(message.timestamp), "MMM d, HH:mm", {
                  locale: dateFnsLocale,
                })
              : new Date(message.timestamp)
          }
          showTimestamp={interactive}
          showAuthor={!grouped}
          hideTimestampUntilHover={isDriverRoomChat}
        >
          {interactive && (
            <Message.Status
              status={status}
              isFromDriver={messageData.isFromDriver}
            />
          )}
        </Message.Header>
        {containsForwardedMessage && <MessageForwarded />}
        {containsReplyMessage && showReplay && <MessageReply />}
        {containsFallbackReplyMessage && showReplay && <MessageReplyFallback />}
        {!containsForwardedMessage && (
          <MessageBody
            content={
              containsOutgoingTranslation
                ? message.originalBody
                : messageData.displayBody
            }
            contentOffset={
              containsOutgoingTranslation
                ? undefined
                : messageData.displayBodyOffset
            }
          />
        )}
        {containsOutgoingTranslation && interactive && (
          <MessageText
            content={message.body}
            className="mt-2 text-violet-600"
          />
        )}
        {containsTranslation &&
          interactive &&
          (browserTranslationEnabled && messageData.isIncoming ? (
            <IncomingTranslation
              key={JSON.stringify([
                message.id,
                message.body,
                message.translation,
              ])}
              chatJid={messageData.activeChat.jid}
              messageId={message.id}
              original={message.body}
              polish={message.translation!}
            />
          ) : (
            <MessageText
              content={message.translation}
              className="mt-2 text-violet-600"
            />
          ))}
        {interactive && !isDispatcherOwnMessage && <Message.Metadata />}
        {containsReactions && interactive && (
          <Message.Reactions reactions={messageData.reactions} />
        )}
      </Message.Bubble>

      {interactive && !isForwardingMode && <Message.Actions />}
    </Message>
  )
}

// Custom comparison function that ignores context changes that don't affect rendering
const areEqual = (
  prevProps: GenericMessageProps,
  nextProps: GenericMessageProps,
) => {
  // Compare all props except context-dependent ones
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.body === nextProps.message.body &&
    prevProps.message.translation === nextProps.message.translation &&
    prevProps.message.originalBody === nextProps.message.originalBody &&
    prevProps.message.timestamp === nextProps.message.timestamp &&
    prevProps.message.internal === nextProps.message.internal &&
    prevProps.message.reactions.length === nextProps.message.reactions.length &&
    prevProps.message.reactions.every(
      (reaction, index) =>
        reaction.id === nextProps.message.reactions[index]?.id &&
        reaction.emojis.join(",") ===
          nextProps.message.reactions[index]?.emojis.join(","),
    ) &&
    prevProps.grouped === nextProps.grouped &&
    prevProps.isFirstInGrouped === nextProps.isFirstInGrouped &&
    prevProps.isLastInGrouped === nextProps.isLastInGrouped &&
    prevProps.interactive === nextProps.interactive &&
    prevProps.showReplay === nextProps.showReplay &&
    prevProps.actionButtonsVisible === nextProps.actionButtonsVisible
  )
}

export default memo(GenericMessage, areEqual)
